import { FC } from 'react';
import { Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { CarListBand, FleetVehicle, Permission, VehicleStatus } from '@services';
import { Grid, Stack, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { GuardedRender } from '@components/portal/common';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';

export interface IVehicleListViewerProps extends IEntityListProps<FleetVehicle> {
	paddingX?: number | string;
	entities: FleetVehicle[];
	selected: FleetVehicle[];
	segments?: CarListBand[];
	onSelectChange?: (selected: FleetVehicle[]) => void;
	onEdit?: (entity: FleetVehicle) => void;
	onDelete?: (entity: FleetVehicle) => void;
}

export const VehicleListViewer: FC<IVehicleListViewerProps> = ({
	paddingX,
	entities,
	selected,
	segments,
	onSelectChange,
	onEdit,
	onDelete,
	...props
}) => {
	const user = useLoggedinUser();
	const hasWritePermission = user?.hasPermission(Permission.VEHICLE_WRITE);
	const handleSelectChange = (status: boolean, entity: FleetVehicle) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => {
				const segment = (segments || []).find(({ id }) => id === entity.segmentId);
				return (
					<EntityListItem
						key={`fleet-vehicle-${entity.id}-${i}`}
						sx={{ paddingRight: paddingX }}
						cardProps={{
							sx: {
								backgroundColor:
									entity.vehicleStatus === VehicleStatus.MISSING_MANDATORY_DATA
										? 'error.200'
										: entity.vehicleStatus === VehicleStatus.PENDING
										? 'warning.50'
										: undefined
							}
						}}
						selected={!!selected.find((s) => s.id === entity.id)}
						notSelectable={!hasWritePermission}
						badge={segment ? <Typography variant="body2">{segment?.label}</Typography> : undefined}
						actions={[
							{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !hasWritePermission },
							{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !hasWritePermission }
						]}
						onSelectChange={(status) => handleSelectChange(status, entity)}
					>
						<Stack direction="column" spacing={1} sx={{ width: 1.0 }}>
							<Grid container>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="vehicle-list.plate-number" />}>
										<Typography>{entity?.plateNumber || 'Info to add'}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="vehicle-list.manufacturer" />}>
										<Typography>{entity.carModel?.brand || ''}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="vehicle-list.model" />}>
										<Typography>{entity.carModel?.model || ''}</Typography>
									</EntityListItemCell>
								</Grid>
								<Grid item xs={3}>
									<EntityListItemCell title={<Tr.Portal path="vehicle-list.driver" />}>
										<GuardedRender
											canRender={(u) => !u.isSellerLoggedInAsManager()}
											fallback={<Typography>-</Typography>}
										>
											<Typography>
												{entity.driverName || entity.driverSurname
													? `${entity.driverName || ''} ${entity.driverSurname || ''}`
													: 'Info to add'}
											</Typography>
										</GuardedRender>
									</EntityListItemCell>
								</Grid>
							</Grid>
							{entity.metadataRequiredFields.length ? (
								<Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'error.main' }}>
									<CancelIcon fontSize="inherit" color="inherit" />
									<Typography variant="body2" color="inherit">
										<Tr.Portal
											path="vehicle-list.rejected-description-required-fields"
											options={{ fields: entity.metadataRequiredFields.map(({ key }) => key).join(', ') }}
										/>
									</Typography>
								</Stack>
							) : null}
							{entity.metadataErrorFields.length ? (
								<Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'error.main' }}>
									<CancelIcon fontSize="inherit" color="inherit" />
									<Typography variant="body2" color="inherit">
										<Tr.Portal
											path="vehicle-list.error-description-required-fields"
											options={{ fields: entity.metadataErrorFields.map(({ key }) => key).join(', ') }}
										/>
									</Typography>
								</Stack>
							) : null}
							{entity.metadateMissingFields.length ? (
								<Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'warning.900' }}>
									<ErrorIcon fontSize="inherit" color="inherit" />
									<Typography variant="body2" color="inherit">
										<Tr.Portal
											path="vehicle-list.missing-description-required-fields"
											options={{ fields: entity.metadateMissingFields.map(({ key }) => key).join(', ') }}
										/>
									</Typography>
								</Stack>
							) : null}
						</Stack>
					</EntityListItem>
				);
			})}
		</EntityList>
	);
};
